import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { bookingApi } from '../../../utils/bookingApi';
import { useFeature } from '../../../hooks/useFeature';
import { PageHeader, Skeleton, EmptyState } from '../../../components/ui';
import '../booking-shared.css';

const dayOf = s => s.date.slice(0, 10);
const localDay = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const mins = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

// Sessions not ended or cancelled, soonest first, and those running now (or
// starting within 15 minutes)
function liveSessions(sessions, now) {
  const today = localDay(now);
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const live = sessions
    .filter(s => !s.cancelledAt)
    .filter(s => dayOf(s) > today || (dayOf(s) === today && mins(s.endTime) >= nowMins))
    .sort((a, b) => dayOf(a).localeCompare(dayOf(b)) || a.startTime.localeCompare(b.startTime));
  const running = live.filter(s => dayOf(s) === today && mins(s.startTime) - 15 <= nowMins);
  return { live, running };
}

// The session whose register a coach most likely wants: one running now (or
// starting within 15 minutes), otherwise the next one to start. Ended and
// cancelled sessions are skipped.
export function pickNextSession(sessions, now = new Date()) {
  const { live, running } = liveSessions(sessions, now);
  return running[running.length - 1] || live[0] || null;
}

// With Session coaches, the coach's own sessions (mine) come first: their
// session running now, else whatever is running (they may be helping), else
// their next session today, else the next session at all.
export function pickRegister(sessions, mine, now = new Date()) {
  const own = liveSessions(mine, now).running;
  const any = liveSessions(sessions, now).running;
  const ownToday = pickNextSession(mine.filter(s => dayOf(s) === localDay(now)), now);
  return own[own.length - 1] || any[any.length - 1] || ownToday || pickNextSession(sessions, now);
}

// /booking/admin/register: opens the next session's register
export default function NextRegister() {
  const [result, setResult] = useState(undefined); // undefined = looking, null = none found
  const coachesOn = useFeature('sessionCoaches');

  useEffect(() => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const month = d => bookingApi.getSessions(d.getFullYear(), d.getMonth() + 1).then(r => r.data).catch(() => []);
    const mine = coachesOn ? bookingApi.getMySessions(1).then(r => r.data).catch(() => []) : Promise.resolve([]);
    // This month first; only look at next month if nothing is left in this one
    Promise.all([month(now), mine])
      .then(async ([sessions, own]) => pickRegister(sessions, own, now) || pickNextSession(await month(next), now))
      .then(setResult);
  }, [coachesOn]);

  if (result) return <Navigate to={`/booking/admin/register/${result.id}`} replace />;
  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader back={{ to: '/admin-hub', label: 'Admin' }} title="Register" />
      {result === undefined
        ? <Skeleton rows={3} label="Finding the next session…" />
        : (
          <EmptyState
            icon={CalendarDaysIcon}
            title="No upcoming sessions"
            text="The register opens here when the next session is due."
            action={<Link to="/booking/admin" className="bk-btn bk-btn--primary">See all sessions</Link>}
          />
        )}
    </div>
  );
}
